import React, { useCallback } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { formatDate } from "utils";
import { Button, Checkbox, Dropdown, FilterTag, Icon } from "components";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { ShareProjectModal } from "components/_Modals/";
import { useMutation } from "urql";
import { DuplicateProjectMut } from "api/mutations";
import { useTranslate } from "hooks";
import { useNavigateToProject } from "hooks/projectHooks";
import { Spinner } from "components/Spinner";
import { useUser } from "stores/user";

import styles from "./ProjectListTableRow.module.scss";
import { useLanguage } from "stores/globalStore";

export const ProjectListTableRow = Scrivito.connect(
  ({
    project,
    statusColors,
    toggleProjectMenu,
    toggleModalStatus,
    toggleDeleteModal,
    handleCheckboxClick,
    isChecked,
    projectItemObj,
    refetchProjectList,
    spinnerId,
  }) => {
    const translate = useTranslate();
    const {
      id: projectId,
      title,
      updatedAt,
      lastEditBy,
      sharedWith,
      status,
    } = project;

    const lang = useLanguage();
    const user = useUser();
    const loadingState = spinnerId === project.id;

    const [_, exeDuplicateMutation] = useMutation(DuplicateProjectMut);

    const onDetailsClick = useCallback(() => {
      Scrivito.navigateTo(() => projectItemObj, { params: { projectId } });
    }, [projectId, projectItemObj]);

    const handleSomethingClicked = useCallback(() => {}, []);

    const handleDuplicate = useCallback(() => {
      exeDuplicateMutation({
        input: {
          id: projectId,
          languageCode: lang,
          recipientEmail: user?.email,
        },
      }).then(() => refetchProjectList());
    }, [exeDuplicateMutation, lang, projectId, refetchProjectList, user]);

    const handleDownloadPopupClick = () => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: title,
        projectId,
      });
    };

    const handleProjectSelect = useCallback(() => {
      handleCheckboxClick(projectId);
    }, [handleCheckboxClick, projectId]);

    const handleMenuOpen = useCallback(() => {
      toggleProjectMenu(project);
    }, [project, toggleProjectMenu]);

    const navigateToProject = useNavigateToProject();

    const handleNavigateToProjectItem = useCallback(() => {
      if (!loadingState) {
        navigateToProject.item(projectId);
      }
    }, [loadingState, navigateToProject, projectId]);

    const toggleShareModal = useCallback(() => {
      NiceModal.show(ShareProjectModal, { project, refetchProjectList }).then();
    }, [project, refetchProjectList]);
    return (
      <>
        <tr>
          <td
            className="d-flex gap-3 align-items-center"
            data-testid={`PROJECT_LIST_PROJECT-${projectId}`}
          >
            {/* TODO: define font-sizes related to rem and replace font16 etc. */}
            <Checkbox checked={isChecked} onChange={handleProjectSelect} />
            <div>
              <div>
                <Scrivito.LinkTag
                  data-testid="ProjectListTableRow_project-item-link"
                  className="w-100"
                  onClick={handleNavigateToProjectItem}
                >
                  <div className="d-flex text-gray-700 fw-bold pb-1">
                    <span className={` ${styles.textWrapEllipsis}`}>
                      {title}
                    </span>
                    <Icon
                      iconName="arrow-up-right-square-regular"
                      className="icon-16 ms-2 text-gray-300"
                      prefix="trox"
                    />
                  </div>
                  <div className="text-gray-400 font14">{projectId}</div>
                </Scrivito.LinkTag>
              </div>
            </div>
          </td>
          <td data-testid={`PROJECT_LIST_LAST_MODIFICATION-${projectId}`}>
            <div className="font16">
              {formatDate(updatedAt, "DD.MM.YYYY", "YYYY-MM-DD")}
            </div>
            <div className="font14 text-gray-400">
              {translate(
                "PROJECT_EDITED_BY",
                lastEditBy.fullName || translate("UNKNOWN"),
              )}
            </div>
          </td>
          <td data-testid={`PROJECT_SHARING_SETTINGS-${projectId}`}>
            <FilterTag
              id={3}
              name={
                sharedWith?.length > 0
                  ? translate("SHARED")
                  : translate("NOT_SHARED")
              }
              icon={
                sharedWith?.length > 0 ? "user-group-regular" : "user-regular"
              }
              onClick={handleSomethingClicked}
            />
          </td>
          <td data-testid={`PROJECT_LIST_STATUS-${projectId}`}>
            <FilterTag
              id={4}
              name={status.status}
              color={statusColors[status.status]}
              onClick={handleSomethingClicked}
            />
          </td>
          <td align="right">
            <div className="position-relative d-flex flex-nowrap w-fit-content">
              <Button
                data-testid={`DETAILS-${projectId}`}
                onClick={onDetailsClick}
                className={cx("me-3 btn btn-outlined", {
                  "btn-outlined-off": loadingState,
                })}
                color="none"
                disabled={isChecked}
              >
                Profile
              </Button>
              {loadingState ? (
                <Spinner className="loader" size="xs" color="primary" />
              ) : (
                <Dropdown
                  btnClassName="btn-option"
                  testId={`DREI_PUNKT_MNU-${projectId}`}
                  disabled={isChecked}
                  options={[
                    {
                      id: "project_status_update",
                      title: translate("PROJECT_STATUS_UPDATE"),
                      onClick: toggleModalStatus,
                      testId: `PROJECT_STATUS_UPDATE-${projectId}`,
                    },
                    {
                      id: "download",
                      title: translate("DOWNLOAD"),
                      onClick: handleDownloadPopupClick,
                    },
                    {
                      id: 2,
                      title: translate("DUPLICATE"),
                      onClick: handleDuplicate,
                    },
                    /* TODO: forthcoming feature (hidden acc US-19953) */
                    {
                      id: 3,
                      title: translate("SHARE"),
                      onClick: toggleShareModal,
                      testId: `SHARE-${projectId}`,
                    },
                    {
                      id: 4,
                      title: translate("DELETE"),
                      onClick: toggleDeleteModal,
                      testId: `DELETE-${projectId}`,
                    },
                  ]}
                  onClick={handleMenuOpen}
                />
              )}
            </div>
          </td>
        </tr>
      </>
    );
  },
);
